import { requestGetDegreeManagement } from 'app/api/degree'
import { DegreeMessageCode } from 'app/api/degree/constant'
import { requestGetAllIndustryManagement } from 'app/api/industry'
import { IndustryMessageCode } from 'app/api/industry/constant'
import { IIndustry } from 'app/api/skill/model/management'
import R from 'app/assets/R'
import { convertArrayStatusFilterToArrayStatusNumber, convertStatusToFilterStatus } from 'app/common/helpers'
import { FunctionPermissionEnum, ModulePermissionEnum } from 'app/common/permission-module'
import { useVerifyPermissionAndRedirect } from 'app/common/use-verify-permission-redirect'
import {
  IResponseFilter,
  initResponseFilterTableManagement,
} from 'app/components/tables/BaseTableManagement/FilterManagementTable/type'
import {
  RenderValueTable,
  StatusTableEnum,
  useHookTableManagement,
} from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { IDataBaseTable } from 'app/components/tables/BaseTableReport/type'
import { initPagination } from 'app/constant'
import { convertHtmlEntitiesToString } from 'app/helpers/string.helper'
import { isEqual } from 'lodash'
import { Pagination, ResponseType } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IParamsGetDegreeManagement,
  IResponseGetDegreeManagement,
} from '../../../api/degree/model/management'
import { Delete } from './components/Delete'
import { Update } from './components/Update'
import { IGeneralDegreeManagementTable } from './type'

export const useHookTable = () => {
  const { t } = useTranslation()
  const [industries, setIndustry] = useState<IIndustry[]>()

  const [filter, setFilter] = useState<IResponseFilter>(
    initResponseFilterTableManagement,
  )

  const { isHavePermissionFunctionAndGoBack } = useVerifyPermissionAndRedirect()

  useEffect(() => {
    isHavePermissionFunctionAndGoBack(
      ModulePermissionEnum.HR,
      FunctionPermissionEnum.DEGREE_READ,
      R.strings.degree,
    )
  }, [t])

  const getDataTable = async (
    pagination: Pagination,
    newFilter?: IResponseFilter,
  ) => {
    const initData = {
      pagination: pagination ?? initPagination,
      data: [],
    } as IDataBaseTable<IGeneralDegreeManagementTable>
    try {
      const status = convertArrayStatusFilterToArrayStatusNumber(
        newFilter?.status ?? filter?.status,
      )
      let params: IParamsGetDegreeManagement = {
        page: pagination?.current,
        pageSize: pagination?.pageSize,
        keyword: newFilter?.search ?? filter?.search,
      }

      params = { ...params, ...newFilter, status }

      const response: IResponseGetDegreeManagement =
        await requestGetDegreeManagement(params)

      if (
        isEqual(
          response?.msgcode,
          DegreeMessageCode.DegreeManagement.successGetData,
        )
      ) {
        const data = response?.data?.rows
        const metadata = response?.data?.metadata

        const startIndex =
          ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10)

        const dataTables = data?.map((item, index) => {
          return {
            key: index + 1,
            ordinalNumber: startIndex + index + 1,
            name: convertHtmlEntitiesToString(item?.name, '-'),
            industry: convertHtmlEntitiesToString(item?.industry.name, '-'),
            _id: item?._id ?? '-',
            status: convertStatusToFilterStatus(item?.status),
          }
        })

        return {
          pagination: {
            current: metadata?.currentPage ?? 1,
            pageSize: pagination?.pageSize,
            total: metadata?.totalItem ?? 0,
          },
          data: dataTables,
        } as IDataBaseTable<IGeneralDegreeManagementTable>
      }

      return initData
    } catch (error) {
      return initData
    }
  }

  const { tableData, selectRowsTable, handleTableChange, fetch, rowSelection } =
    useHookTableManagement({ getDataTable })

  const handleChangeFilter = (newFilter: IResponseFilter) => {
    if (!isEqual(newFilter, filter)) {
      setFilter(newFilter)
      fetch(
        {
          ...tableData.pagination,
          current: initPagination.current,
        },
        newFilter,
      )
    }
  }

  const fetchNewData = () => {
    fetch(initPagination)
    setFilter(initResponseFilterTableManagement)
  }

  const getAllIndustry = async () => {
    try {
      const res: ResponseType<IIndustry[]> =
        await requestGetAllIndustryManagement()

      if (
        isEqual(
          res?.msgcode,
          IndustryMessageCode.IndustryManagement.successGetData,
        )
      ) {
        const data: IIndustry[] = res?.data || []
        setIndustry(data)
      }
    } catch (error) {
     // console.log({ error })
    }
  }

  useEffect(() => {
    getAllIndustry()
  }, [])

  const columns = useMemo(() => {
    const option = [
      convertColumnTable<IGeneralDegreeManagementTable>({
        title: t(R.strings.ordinal_number),
        key: 'ordinalNumber',
        fixed: 'left',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => (
          <RenderValueTable value={record.ordinalNumber} type="Number" />
        ),
      }),
      convertColumnTable<IGeneralDegreeManagementTable>({
        title: t(R.strings.degree_content_name),
        key: 'name',
        fixed: 'left',
        classNameWidthColumnOverwrite: 'big-column',
        render: (_, record) => (
          <RenderValueTable value={record.name} type="Main" />
        ),
      }),
      convertColumnTable<IGeneralDegreeManagementTable>({
        title: t(R.strings.degree_content_industry),
        key: 'industry',
        render: (_, record) => (
          <RenderValueTable value={record.industry} type="Base" />
        ),
      }),
      convertColumnTable<IGeneralDegreeManagementTable>({
        title: t(R.strings.status),
        key: 'status',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'small-column',
        render: (_, record) => (
          <RenderValueTable
            value={record.status}
            type="Status"
            actionComponent={
              <>
                <Update id={record?._id} fetchTable={fetchNewData} />
                {!isEqual(record?.status, StatusTableEnum.deleted) && (
                  <Delete
                    id={record?._id}
                    fetchTable={fetchNewData}
                    name={record?.name}
                  />
                )}
              </>
            }
          />
        ),
      }),
    ]
    return option
  }, [t, fetchNewData])

  return {
    tableData,
    selectRowsTable,
    handleTableChange,
    fetchNewData,
    rowSelection,
    columns,
    handleChangeFilter,
    filter,
    industries,
  }
}
